// eslint-disable-next-line @nx/enforce-module-boundaries
import { getStyleClassPerTheme, ImageAspectRatio } from '@marriott/mi-ui-library';
import {
  DcaMetadata,
  LayeredFeatureAdapterCtx,
  LayeredFeatureCfContent,
  LayeredFeatureCfPayload,
  LayeredFeatureContentPayload,
  LayeredFeatureDcaProps,
  LayeredFeatureDCAPayload,
  LayeredFeatureImage,
  isCoBrandDca,
  isDcaContent,
} from './adapter.types';

const MATH_FIELDS = ['mathLabel1', 'mathLabel2', 'mathLabel3', 'mathValue', 'mathType', 'mathTitle'] as const;

function getCfDescription(cf: LayeredFeatureCfContent): string {
  const { description } = cf;
  if (typeof description === 'string') return description;
  if (description?.plaintext) return description.plaintext;
  return '';
}

function getImageAssetPath(content: LayeredFeatureContentPayload, fallbackAssetPath: string): string {
  if ('featureImage' in content && content.featureImage?.image?.assetPath) {
    return content.featureImage.image.assetPath;
  }
  if ('image' in content && content.image) {
    const { assetPath, imageSrc, imageUrls } = content.image;
    return (
      assetPath ??
      imageSrc ??
      imageUrls?.square ??
      imageUrls?.standardHorizontal ??
      imageUrls?.wideHorizontal ??
      fallbackAssetPath
    );
  }
  if ('assetPath' in content && content.assetPath) return content.assetPath;
  return fallbackAssetPath;
}

function getImageAltText(content: LayeredFeatureContentPayload, fallbackAlt: string): string {
  if ('imageAltText' in content && content.imageAltText) return content.imageAltText;
  if ('image' in content && content.image?.alternateDescription) {
    return content.image.alternateDescription;
  }
  return fallbackAlt;
}

function resolveBackgroundImage(
  content: LayeredFeatureContentPayload,
  fallbackAssetPath: string,
  fallbackAlt: string
): LayeredFeatureImage {
  if ('featureImage' in content && content.featureImage?.image?.assetPath) {
    return content.featureImage;
  }

  const assetPath = getImageAssetPath(content, fallbackAssetPath);
  const altText = getImageAltText(content, fallbackAlt);

  return {
    image: {
      assetPath,
      altText,
    },
  };
}

function adaptContentFragmentToDcaProps(ctx: LayeredFeatureCfPayload): LayeredFeatureDcaProps {
  const cf = ctx.contentObj;
  const backgroundImage = resolveBackgroundImage(ctx.contentObj, ctx.baseUrl, ctx.alt);
  const header = cf.header ?? cf.title ?? '';
  const description = getCfDescription(cf);
  const className = cf.styleclass ?? cf.styleClass ?? '';

  return {
    eyebrowText: cf.eyebrow ?? '',
    headerLong: header,
    headerMedium: header,
    headerShort: header,
    descriptionLong: description,
    descriptionMedium: description,
    descriptionShort: description,
    callToActionText1: cf.primaryCtaText ?? cf.callToActionText1,
    destinationUrl1: cf.primaryCtaLink ?? cf.destinationUrl1,
    callToActionText2: cf.secondaryCtaText ?? cf.callToActionText2,
    destinationUrl2: cf.secondaryCtaLink ?? cf.destinationUrl2,
    openInNewTab: cf.primaryCtaOpenInNewTab ?? cf.openInNewTab,
    tertiaryLinkText: cf.tertiaryLinkText,
    tertiaryLinkUrl: cf.tertiaryLinkUrl,
    tertiaryLinkOpenInNewTab: cf.tertiaryLinkOpenInNewTab,
    backgroundImage,
    desktopVariation: cf.desktopVariation ?? ImageAspectRatio.SIZE_3x1,
    tabletVariation: cf.tabletVariation ?? ImageAspectRatio.SIZE_2X1,
    mobileVariation: cf.mobileVariation ?? ImageAspectRatio.SIZE_1x1,
    className: className || undefined,
    logoImage: cf.brandLogoIcon,
    componentId: cf.componentId,
    trackingProperties: cf.trackingProperties,
    custom_click_track_value_primary: cf.custom_click_track_value_primary,
  };
}

function adaptDcaBaseToDcaProps(ctx: LayeredFeatureDCAPayload): LayeredFeatureDcaProps {
  const dca = ctx.contentObj;
  const backgroundImage = resolveBackgroundImage(ctx.contentObj, ctx.baseUrl, ctx.alt);

  return {
    eyebrowText: dca.eyebrowText ?? '',
    headerLong: dca.headerLong,
    headerMedium: dca.headerMedium,
    headerShort: dca.headerShort,
    descriptionLong: dca.descriptionLong,
    descriptionMedium: dca.descriptionMedium,
    descriptionShort: dca.descriptionShort,
    callToActionText1: dca.callToActionText1,
    destinationUrl1: dca.destinationUrl1,
    callToActionText2: dca.callToActionText2,
    destinationUrl2: dca.destinationUrl2,
    openInNewTab: dca.openInNewTab,
    backgroundImage,
    desktopVariation: ImageAspectRatio.SIZE_3x1,
    tabletVariation: ImageAspectRatio.SIZE_2X1,
    mobileVariation: ImageAspectRatio.SIZE_1x1,
  };
}

function hasNonEmptyMathValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.some(entry => hasNonEmptyMathValue(entry));
  return true;
}

export function hasMathValues(contentObj: Record<string, unknown>): boolean {
  return MATH_FIELDS.some(field => hasNonEmptyMathValue(contentObj[field]));
}

export function pickLayeredFeaturePropsConfiguration(
  ctx: LayeredFeatureAdapterCtx
): (ctx: LayeredFeatureAdapterCtx) => LayeredFeatureDcaProps {
  if (isCoBrandDca(ctx)) {
    return adaptDcaCoBrandToLayeredFeature;
  } else if (isDcaContent(ctx)) {
    return adaptDcaBaseToLayeredFeature;
  }
  return adaptContentFragmentToLayeredFeature;
}

export function adaptContentFragmentToLayeredFeature(ctx: LayeredFeatureCfPayload): LayeredFeatureDcaProps {
  return adaptContentFragmentToDcaProps(ctx);
}

export function adaptDcaBaseToLayeredFeature(ctx: LayeredFeatureDCAPayload): LayeredFeatureDcaProps {
  return adaptDcaBaseToDcaProps(ctx);
}

export function adaptDcaCoBrandToLayeredFeature(ctx: LayeredFeatureDCAPayload): LayeredFeatureDcaProps {
  const dca = ctx.contentObj;
  const metadata = dca.metadata as DcaMetadata | undefined;
  const baseProps = adaptDcaBaseToDcaProps(ctx);

  return {
    ...baseProps,
    logoImage: dca.logoAssetId ?? dca.imageAssetId ?? undefined,
    mathType: dca.mathType ?? undefined,
    mathLabel1: dca.mathLabel1 ?? undefined,
    mathLabel2: dca.mathLabel2 ?? undefined,
    mathLabel3: dca.mathLabel3 ?? undefined,
    mathValue: dca.mathValue ?? undefined,
    mathTitle: dca.mathTitle ?? undefined,
    legalLong: dca.legalLong ?? undefined,
    metadata: metadata ?? undefined,
    mboxParameter: dca.mboxName ?? undefined,
    // className: metadata?.theme?.toLowerCase() === 'dark' ? 'inverse' : undefined,
    className: getStyleClassPerTheme(metadata?.theme) || undefined,
  };
}
